import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Lock, Sparkles } from 'lucide-react';
type Props = {
  isOpen: boolean;
  onClose: () => void;
  bundleUrl: string;
  mainOnlyUrl: string;
};
export function ExitUpsellModal({
  isOpen,
  onClose,
  bundleUrl,
  mainOnlyUrl
}: Props) {
  // Lock body scroll when open + escape to close
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-50">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto bg-navy-900/85 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-51">
          <motion.div initial={{
        opacity: 0,
        scale: 0.92,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="relative w-full max-w-[560px] bg-navy-800 border-2 border-gold rounded-[20px] shadow-[0_20px_80px_rgba(0,0,0,0.6)] overflow-hidden my-auto" onClick={e => e.stopPropagation()} data-id="element-52">
            {/* Close button */}
            <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition-colors" data-id="element-53">
              <X className="w-5 h-5" data-id="element-54" />
            </button>

            {/* Header banner */}
            <div className="bg-gradient-to-r from-navy-900 via-teal-600/30 to-navy-900 px-6 pt-7 pb-6 text-center border-b border-white/10" data-id="element-55">
              <div className="inline-flex items-center gap-2 bg-red-500/15 border border-red-500/40 text-red-300 uppercase text-[10px] font-bold tracking-widest px-3 py-1 rounded-full mb-4" data-id="element-56">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" data-id="element-57" />
                Wait — Don't Miss This
              </div>
              <h2 id="upsell-title" className="font-serif text-white text-[26px] md:text-[32px] font-bold leading-[1.15] mb-2" data-id="element-58">
                Hold on — before you check out 🛑
              </h2>
              <p className="text-teal-300 text-[14px] md:text-[15px] font-medium" data-id="element-59">
                You're leaving the best part of the deal behind.
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-7 space-y-6" data-id="element-60">
              {/* Flashing discount badge */}
              <div className="flex justify-center" data-id="element-61">
                <motion.div animate={{
              opacity: [1, 0.55, 1],
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="inline-flex items-center gap-2 bg-gold text-navy-900 px-5 py-2 rounded-full font-black text-[14px] uppercase tracking-wide shadow-[0_0_30px_rgba(201,168,76,0.6)]" data-id="element-62">
                  <Sparkles className="w-4 h-4" data-id="element-63" />
                  30% Off Both Add-Ons — Tonight Only
                </motion.div>
              </div>

              {/* Main copy */}
              <div className="text-center space-y-3" data-id="element-64">
                <p className="text-white text-[16px] leading-relaxed" data-id="element-65">
                  Add{' '}
                  <span className="text-teal-300 font-semibold" data-id="element-66">
                    both rescue add-ons
                  </span>{' '}
                  right now and we'll knock{' '}
                  <span className="text-gold font-bold" data-id="element-67">
                    30% off the bundle
                  </span>{' '}
                  — so tonight you wake up to a calmer back, a quieter mind, and
                  a body that actually let you sleep.
                </p>
                <p className="text-white/70 text-[14px] leading-relaxed" data-id="element-68">
                  This is the complete nightly system — the protocol, the 3 AM
                  rescue plan, and the anti-inflammatory ritual. All instantly
                  delivered.
                </p>
              </div>

              {/* What's included */}
              <div className="bg-white/5 border border-white/10 rounded-[14px] p-5 space-y-2.5" data-id="element-69">
                <p className="text-teal-400 text-[11px] uppercase tracking-widest font-bold mb-2" data-id="element-70">
                  Your Complete Package Includes:
                </p>
                {[{
              title: 'The 11PM Spine Reset',
              desc: 'The full 12-minute floor protocol'
            }, {
              title: '+ 3 Free Bonuses',
              desc: 'Checklist, Bed Exit Guide & Consistency Tracker'
            }, {
              title: 'The 3 AM Emergency Toolkit',
              desc: 'Shut down sudden flare-ups in 60 seconds'
            }, {
              title: 'The Anti-Inflammatory Nightcap',
              desc: '7 healing recipes to quiet the night-fire'
            }].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-71">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-teal-400/20 border border-teal-400 flex items-center justify-center" data-id="element-72">
                      <Check className="w-3 h-3 text-teal-300" strokeWidth={3} data-id="element-73" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-74">
                      <p className="text-white text-[14px] font-semibold leading-tight" data-id="element-75">
                        {item.title}
                      </p>
                      <p className="text-white/55 text-[12px] leading-snug mt-0.5" data-id="element-76">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>

              {/* Pricing */}
              <div className="text-center space-y-1" data-id="element-77">
                <div className="flex items-center justify-center gap-3" data-id="element-78">
                  <span className="text-white/40 text-[18px] line-through font-semibold" data-id="element-79">
                    $25.85
                  </span>
                  <span className="font-serif text-gold text-[40px] md:text-[48px] font-black leading-none" data-id="element-80">
                    $21.06
                  </span>
                </div>
                <div className="flex items-center justify-center gap-1.5 text-white/50 text-[11px] italic" data-id="element-81">
                  <Lock className="w-3 h-3 text-teal-400" data-id="element-82" />
                  Special price only available on this screen
                </div>
              </div>

              {/* CTAs */}
              <div className="space-y-3 pt-1" data-id="element-83">
                <motion.a href={bundleUrl} target="_blank" rel="noopener noreferrer" animate={{
              boxShadow: ['0 0 0 0 rgba(45,212,191,0)', '0 0 0 8px rgba(45,212,191,0.15)', '0 0 0 0 rgba(45,212,191,0)']
            }} transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="block w-full text-center bg-teal-400 hover:bg-teal-300 text-navy-900 font-black text-[15px] md:text-[16px] uppercase px-5 py-4 rounded-[12px] tracking-wide leading-snug transition-colors" data-id="element-84">
                  YES! I Want The Complete Package For $21.06 →
                </motion.a>
                <a href={mainOnlyUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center text-white/55 hover:text-white text-[13px] underline underline-offset-2 transition-colors" data-id="element-85">
                  No thanks, continue without the extras →
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}